import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Paper, Stack, LinearProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

const PreencherPlanilhaProducao = () => {
  const [file, setFile] = useState(null);
  const [processedFileBlob, setProcessedFileBlob] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  const { authenticatedFetch, logout } = useAuth();

  // Função para enviar o arquivo para o backend
  const processAndDownloadFile = async (selectedFile) => {
    setIsLoading(true);
    setProcessedFileBlob(null); // Limpa o estado do arquivo processado
    setError('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      // Substitua 'SUA_URL_DO_BACKEND/process-file' pela sua URL real
      const response = await authenticatedFetch('' + API_BASE_URL + '/api/processar-planilha/', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao processar o arquivo.');
      }

      const blob = await response.blob();
      setProcessedFileBlob(blob);
      setError('Arquivo processado com sucesso. Clique em Baixar.');
      
    } catch (err) {
      if (err.message.includes('Token')) {
        logout();
      } else {
        alert('Erro: ' + err.message);
      }
      setFile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event) => {
    
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      const fileName = uploadedFile.name;
      const regex = /^\d{4}-\d{2}-\d{2} planejamento diario - (segunda-feira|terça-feira|quarta-feira|quinta-feira|sexta-feira|sábado|domingo)\.xlsx$/i;

      if (regex.test(fileName)) {
        setFile(uploadedFile);
        processAndDownloadFile(uploadedFile); // Inicia o processamento
      } else {
        setFile(null);
        setError('Formato de nome de arquivo inválido. Por favor, use o padrão "AAAA-MM-DD planejamento diario - {dia-da-semana}.xlsx".');
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (isLoading) return;

    const uploadedFile = event.dataTransfer.files[0];
    if (uploadedFile) {
      const fileName = uploadedFile.name;
      const regex = /^\d{4}-\d{2}-\d{2} planejamento diario - (segunda-feira|terça-feira|quarta-feira|quinta-feira|sexta-feira|sábado|domingo)\.xlsx$/i;

      if (regex.test(fileName)) {
        setFile(uploadedFile);
        processAndDownloadFile(uploadedFile); // Inicia o processamento
      } else {
        setFile(null);
        setError('Formato de nome de arquivo inválido. Por favor, use o padrão "AAAA-MM-DD planejamento diario - {dia-da-semana}.xlsx".');
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Função para abrir o seletor de arquivos quando clicar no Paper
  const handlePaperClick = () => {
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownload = () => {
    if (processedFileBlob && file) {
      const url = window.URL.createObjectURL(new Blob([processedFileBlob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Limpa os estados após o download
      setFile(null);
      setProcessedFileBlob(null);
      setError('');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Antes em Ler CSV no menu lateral, gerar relatório com a data do dia a preencher a planilha de planejamento diário.
      </Typography>

      <Typography variant="body1" sx={{ mb: 1 }}>
        Selecione o arquivo e arraste:
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: isLoading ? 'default' : 'pointer',
          border: '2px dashed',
          borderColor: error ? 'error.main' : (file ? 'success.main' : 'text.secondary'),
          '&:hover': {
            borderColor: isLoading ? 'text.secondary' : 'primary.main',
            backgroundColor: isLoading ? 'background.paper' : 'action.hover',
          },
          backgroundColor: 'background.paper',
          position: 'relative',
          transition: 'all 0.2s ease-in-out',
        }}
        onClick={handlePaperClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          type="file"
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx, .xls"
        />
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
        >
          <CloudUploadIcon 
            sx={{ 
              fontSize: 40, 
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
            }} 
          />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {file ? file.name : 'Clique aqui ou arraste e solte o arquivo'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Limite de 200MB por arquivo .XLSX
          </Typography>
        </Stack>
        {isLoading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Processando arquivo...
            </Typography>
          </Box>
        )}
      </Paper>
      
      {error && (
        <Typography 
          variant="body2" 
          color={processedFileBlob ? 'success' : 'error'} 
          sx={{ mt: 2, textAlign: 'center' }}
        >
          {error}
        </Typography>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={!processedFileBlob || isLoading}
          sx={{ minWidth: 200 }}
        >
          Baixar Arquivo Editado
        </Button>
      </Box>
    </Box>
  );
};

export default PreencherPlanilhaProducao;